/**
 * Flickity-compatible adapter on top of Embla Carousel.
 *
 * Exposes a global `Flickity` (and `window.fizzyUIUtils.modulo`) so the existing
 * theme code in slideshow.js / product.js / collection-tabs.js keeps working
 * without a rewrite, while the actual carousel engine is Embla.
 *
 * Only the Flickity surface actually used by these themes is implemented:
 * constructor options (wrapAround, cellAlign, contain, fade, autoPlay,
 * rightToLeft, draggable, asNavFor, adaptiveHeight, pageDots, cellSelector,
 * initialIndex, on{}), the events ready/change/settle/scroll/staticClick, the
 * methods select/selectCell/previous/next/resize/reposition/destroy/insert/
 * reloadCells/updateDraggable + autoplay player controls, the static
 * Flickity.data(), and the props selectedIndex/selectedElement/slides/cells/
 * player/options.
 */
(function (window, document) {
  'use strict';

  if (window.Flickity) {
    return;
  }

  var Embla = window.EmblaCarousel;
  var AutoplayPlugin = window.EmblaCarouselAutoplay;
  var FadePlugin = window.EmblaCarouselFade;
  var AutoHeightPlugin = window.EmblaCarouselAutoHeight;

  if (!Embla) {
    // Embla bundle missing: fail loudly in the console but don't throw so the
    // rest of the page keeps working.
    if (window.console) {
      window.console.error('[carousel-adapter] EmblaCarousel not found. Did vendor.min.js load?');
    }
    return;
  }

  // slideshow.js relies on fizzyUIUtils.modulo (previously provided by Flickity).
  if (!window.fizzyUIUtils) {
    window.fizzyUIUtils = {};
  }
  if (!window.fizzyUIUtils.modulo) {
    window.fizzyUIUtils.modulo = function (a, b) {
      return ((a % b) + b) % b;
    };
  }

  var registry = [];

  function findIndex(arr, node) {
    for (var i = 0; i < arr.length; i++) {
      if (arr[i] === node) {
        return i;
      }
    }
    return -1;
  }

  function toArray(nodeList) {
    return Array.prototype.slice.call(nodeList);
  }

  function Flickity(element, options) {
    if (typeof element === 'string') {
      element = document.querySelector(element);
    }
    if (!element) {
      return;
    }
    this.element = element;
    this.options = options || {};
    this._handlers = {};
    this._dotsEl = null;
    this._destroyed = false;
    this._build();
    registry.push(this);
  }

  Flickity.data = function (element) {
    if (typeof element === 'string') {
      element = document.querySelector(element);
    }
    for (var i = 0; i < registry.length; i++) {
      if (registry[i].element === element) {
        return registry[i];
      }
    }
    return undefined;
  };

  var proto = Flickity.prototype;

  proto._cellSelector = function () {
    return this.options.cellSelector || '.carousel__slide';
  };

  proto._collectCells = function () {
    return toArray(this.element.querySelectorAll(this._cellSelector()));
  };

  proto._alignValue = function () {
    // Flickity's default cellAlign is 'center'; preserve that when unset
    // (e.g. the product gallery relies on centered peeking on mobile).
    var align = this.options.cellAlign === undefined ? 'center' : this.options.cellAlign;
    if (align === 'center') {
      return 'center';
    }
    if (align === 'right') {
      return 'end';
    }
    return 'start';
  };

  proto._build = function () {
    var self = this;
    var element = this.element;
    var cells = this._collectCells();
    this.cellElements = cells;

    var viewport = document.createElement('div');
    viewport.className = 'flickity-viewport';
    var slider = document.createElement('div');
    slider.className = 'flickity-slider';
    viewport.appendChild(slider);

    if (cells.length && cells[0].parentNode === element) {
      element.insertBefore(viewport, cells[0]);
    } else {
      element.insertBefore(viewport, element.firstChild);
    }
    cells.forEach(function (cell) {
      slider.appendChild(cell);
    });

    this.viewport = viewport;
    this.slider = slider;

    var draggable = this.options.draggable !== false;
    element.classList.add('flickity-enabled');
    if (draggable) {
      element.classList.add('is-draggable');
    }
    if (this.options.rightToLeft) {
      element.classList.add('flickity-rtl');
    }
    if (this.options.fade) {
      element.classList.add('is-fade');
    }

    var startIndex = 0;
    if (typeof this.options.initialIndex === 'number') {
      startIndex = this.options.initialIndex;
    } else if (typeof this.options.initialIndex === 'string') {
      var initialEl = element.querySelector(this.options.initialIndex);
      var initialIdx = findIndex(cells, initialEl);
      if (initialIdx > -1) {
        startIndex = initialIdx;
      }
    }

    var emblaOptions = {
      container: slider,
      slides: cells,
      loop: !!this.options.wrapAround,
      align: this._alignValue(),
      containScroll: this.options.contain === false ? false : 'trimSnaps',
      direction: this.options.rightToLeft ? 'rtl' : 'ltr',
      watchDrag: draggable,
      startIndex: startIndex,
      duration: 22
    };

    var plugins = [];
    if (this.options.fade && FadePlugin) {
      plugins.push(FadePlugin());
    }
    if (this.options.adaptiveHeight && AutoHeightPlugin) {
      plugins.push(AutoHeightPlugin());
    }
    if (this.options.autoPlay && AutoplayPlugin) {
      plugins.push(AutoplayPlugin({
        delay: typeof this.options.autoPlay === 'number' ? this.options.autoPlay : 4000,
        stopOnInteraction: false,
        stopOnMouseEnter: true,
        stopOnFocusIn: true
      }));
    }

    var embla = Embla(viewport, emblaOptions, plugins);
    this.embla = embla;
    this.autoplay = (embla.plugins() && embla.plugins().autoplay) || null;

    this._syncSelectedClass();
    embla.on('select', function () {
      self._syncSelectedClass();
      self._updateDots();
      self._emit('change', embla.selectedScrollSnap());
    });
    embla.on('settle', function () {
      self._emit('settle', embla.selectedScrollSnap());
    });
    embla.on('scroll', function () {
      self._emit('scroll', embla.scrollProgress());
    });

    // Flickity's `staticClick` fires on a click that wasn't a drag. Embla v8 has
    // no `clickAllowed`, so detect a non-drag click via pointer movement.
    var pointerStart = null;
    viewport.addEventListener('pointerdown', function (event) {
      pointerStart = { x: event.clientX, y: event.clientY };
    }, true);
    viewport.addEventListener('click', function (event) {
      var isDrag = false;
      if (pointerStart) {
        isDrag = Math.abs(event.clientX - pointerStart.x) > 5 ||
          Math.abs(event.clientY - pointerStart.y) > 5;
        pointerStart = null;
      }
      if (!isDrag) {
        self._emit('staticClick', event, null, event.target, embla.selectedScrollSnap());
      }
    });

    if (this.options.pageDots) {
      this._buildDots();
    }

    if (this.options.on) {
      Object.keys(this.options.on).forEach(function (eventName) {
        self.on(eventName, self.options.on[eventName]);
      });
    }

    if (this.options.asNavFor) {
      this._setupAsNavFor(this.options.asNavFor);
    }

    // Flickity fires `ready` after init; Embla schedules `init` on the next
    // tick, so deferring keeps the relative order (init -> ready) intact.
    setTimeout(function () {
      if (!self._destroyed) {
        self._emit('ready');
      }
    }, 0);
  };

  proto._syncSelectedClass = function () {
    if (!this.embla) {
      return;
    }
    var selected = this.embla.selectedScrollSnap();
    this.embla.slideNodes().forEach(function (node, index) {
      node.classList.toggle('is-selected', index === selected);
    });
  };

  proto._buildDots = function () {
    var self = this;
    var list = document.createElement('ol');
    list.className = 'flickity-page-dots';
    var snaps = this.embla.scrollSnapList();
    snaps.forEach(function (_, index) {
      var dot = document.createElement('li');
      dot.className = 'dot';
      dot.setAttribute('aria-label', 'Page dot ' + (index + 1));
      dot.addEventListener('click', function () {
        self.embla.scrollTo(index);
      });
      list.appendChild(dot);
    });
    this.element.appendChild(list);
    this._dotsEl = list;
    this._updateDots();
  };

  proto._updateDots = function () {
    if (!this._dotsEl || !this.embla) {
      return;
    }
    var selected = this.embla.selectedScrollSnap();
    toArray(this._dotsEl.children).forEach(function (dot, index) {
      dot.classList.toggle('is-selected', index === selected);
    });
  };

  proto._removeDots = function () {
    if (this._dotsEl && this._dotsEl.parentNode) {
      this._dotsEl.parentNode.removeChild(this._dotsEl);
    }
    this._dotsEl = null;
  };

  proto._setupAsNavFor = function (navFor) {
    var self = this;
    var navElement = typeof navFor === 'string' ? document.querySelector(navFor) : navFor;
    if (!navElement) {
      return;
    }
    var attempts = 0;
    var attach = function () {
      var target = Flickity.data(navElement);
      if (!target || !target.embla) {
        if (attempts++ < 40) {
          setTimeout(attach, 50);
        }
        return;
      }
      self._navTarget = target;
      var syncing = false;
      target.embla.on('select', function () {
        if (syncing) {
          return;
        }
        syncing = true;
        self.embla.scrollTo(target.embla.selectedScrollSnap());
        syncing = false;
      });
      self.embla.on('select', function () {
        if (syncing) {
          return;
        }
        syncing = true;
        target.embla.scrollTo(self.embla.selectedScrollSnap());
        syncing = false;
      });
      // Clicking a slide in the nav carousel selects it (and thus the target).
      self.embla.slideNodes().forEach(function (node, index) {
        node.addEventListener('click', function () {
          self.embla.scrollTo(index);
        });
      });
    };
    attach();
  };

  proto.on = function (eventName, callback) {
    if (typeof callback !== 'function') {
      return this;
    }
    (this._handlers[eventName] = this._handlers[eventName] || []).push(callback);
    return this;
  };

  proto.off = function (eventName, callback) {
    var handlers = this._handlers[eventName];
    if (!handlers) {
      return this;
    }
    var index = findIndex(handlers, callback);
    if (index > -1) {
      handlers.splice(index, 1);
    }
    return this;
  };

  proto._emit = function (eventName) {
    var handlers = this._handlers[eventName];
    if (!handlers || !handlers.length) {
      return;
    }
    var args = Array.prototype.slice.call(arguments, 1);
    var self = this;
    handlers.slice().forEach(function (handler) {
      handler.apply(self, args);
    });
  };

  proto.select = function (index) {
    if (this.embla) {
      this.embla.scrollTo(index);
    }
  };

  proto.selectCell = function (value) {
    if (!this.embla) {
      return;
    }
    var index = -1;
    if (typeof value === 'number') {
      index = value;
    } else {
      var node = typeof value === 'string' ? this.element.querySelector(value) : value;
      index = findIndex(this.embla.slideNodes(), node);
    }
    if (index > -1) {
      this.embla.scrollTo(index);
    }
  };

  proto.previous = function () {
    if (this.embla) {
      this.embla.scrollPrev();
    }
  };

  proto.next = function () {
    if (this.embla) {
      this.embla.scrollNext();
    }
  };

  proto.resize = function () {
    if (this.embla) {
      this.embla.reInit();
      this._syncSelectedClass();
    }
  };

  proto.reposition = function () {
    this.resize();
  };

  // The autoplay plugin no-ops its init when there are <= 1 snaps, after which
  // its play/stop can throw. Guard every call so a single-slide carousel with
  // autoplay enabled can never break the page.
  proto._autoplay = function (method) {
    if (!this.autoplay || !this.embla) {
      return;
    }
    try {
      if (this.embla.scrollSnapList().length <= 1) {
        return;
      }
      this.autoplay[method]();
    } catch (error) {
      /* no-op */
    }
  };

  proto.stopPlayer = function () {
    this._autoplay('stop');
  };

  proto.playPlayer = function () {
    this._autoplay('play');
  };

  proto.pausePlayer = function () {
    this._autoplay('stop');
  };

  proto.unpausePlayer = function () {
    this._autoplay('play');
  };

  proto.updateDraggable = function () {
    if (this.embla) {
      this.embla.reInit({ watchDrag: this.options.draggable !== false });
    }
  };

  // Dynamic cells (used by collection-tabs.js tab switching).
  proto.insert = function (elements) {
    if (!this.slider) {
      return;
    }
    var slider = this.slider;
    toArray(elements).forEach(function (element) {
      slider.appendChild(element);
    });
  };

  proto.reloadCells = function () {
    if (!this.embla) {
      return;
    }
    var element = this.element;
    var slider = this.slider;
    var matched = toArray(element.querySelectorAll(this._cellSelector()));

    matched.forEach(function (cell) {
      slider.appendChild(cell);
    });
    toArray(slider.children).forEach(function (child) {
      if (findIndex(matched, child) === -1) {
        element.appendChild(child);
      }
    });

    this.cellElements = matched;
    this.embla.reInit({ container: slider, slides: matched });
    this._syncSelectedClass();
    this._updateDots();
  };

  proto.destroy = function () {
    this._destroyed = true;
    if (this.embla) {
      this.embla.destroy();
    }
    var element = this.element;
    var slider = this.slider;
    var viewport = this.viewport;
    if (slider && viewport) {
      while (slider.firstChild) {
        element.insertBefore(slider.firstChild, viewport);
      }
      if (viewport.parentNode) {
        viewport.parentNode.removeChild(viewport);
      }
    }
    element.classList.remove('flickity-enabled', 'is-fade', 'is-draggable', 'flickity-rtl');
    this._removeDots();
    var index = findIndex(registry, this);
    if (index > -1) {
      registry.splice(index, 1);
    }
    this.embla = null;
  };

  Object.defineProperty(proto, 'selectedIndex', {
    get: function () {
      return this.embla ? this.embla.selectedScrollSnap() : 0;
    }
  });

  Object.defineProperty(proto, 'selectedElement', {
    get: function () {
      if (!this.embla) {
        return null;
      }
      return this.embla.slideNodes()[this.embla.selectedScrollSnap()] || null;
    }
  });

  Object.defineProperty(proto, 'slides', {
    get: function () {
      return this.embla ? this.embla.slideNodes() : [];
    }
  });

  Object.defineProperty(proto, 'cells', {
    get: function () {
      if (!this.embla) {
        return [];
      }
      return this.embla.slideNodes().map(function (node) {
        return {
          element: node,
          size: { height: node.offsetHeight, width: node.offsetWidth }
        };
      });
    }
  });

  Object.defineProperty(proto, 'player', {
    get: function () {
      var autoplay = this.autoplay;
      var playing = autoplay && autoplay.isPlaying && autoplay.isPlaying();
      return { state: playing ? 'playing' : 'paused' };
    }
  });

  window.Flickity = Flickity;
})(window, document);
